// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TranslationFn = (key: any) => string;
import { themeColors } from "../components/chartTheme";
import { calculateDynamicYDomain } from "./chartHelpers";
import type {
  ExerciseChartDataPoint,
  SleepChartDataPoint,
} from "../components/LifestyleThreeZoneTrendChart";

const EXERCISE_Y_DOMAIN: [number, number] = [0, 400];
const SLEEP_Y_DOMAIN: [number, number] = [0, 12];

/**
 * Returns the display value for an exercise data point.
 */
export function getExerciseDisplayValue(
  point: ExerciseChartDataPoint,
  t: TranslationFn
): string {
  if (point.valueDisplayLabel) {
    return point.valueDisplayLabel;
  }
  if (point.minutesPerWeek >= 300) {
    return t("HealthChart.exerciseChart.value300Plus");
  }
  return String(point.minutesPerWeek);
}

/**
 * Returns the display value for a sleep data point.
 */
export function getSleepDisplayValue(
  point: SleepChartDataPoint,
  t: TranslationFn
): string {
  if (point.valueDisplayLabel) {
    return point.valueDisplayLabel;
  }
  if (point.sleepHours > 9) {
    return t("HealthChart.sleepChart.valueOver9");
  }
  if (point.sleepHours >= 7) {
    return "7-9";
  }
  return "<7";
}

/**
 * Formats Y-axis tick labels for exercise/sleep charts.
 * Only shows values at zone boundaries.
 */
export function formatYAxisTick(
  value: number,
  variant: "exercise" | "sleep"
): string {
  if (variant === "exercise") {
    if (value === 150 || value === 300) {
      return String(value);
    }
    return "";
  }
  if (value === 7 || value === 9) {
    return String(value);
  }
  return "";
}

/**
 * Computes the Y-axis domain for exercise or sleep chart data.
 */
export function computeYDomain(
  variant: "exercise" | "sleep",
  data: ExerciseChartDataPoint[] | SleepChartDataPoint[]
): [number, number] {
  if (variant === "exercise") {
    return calculateDynamicYDomain(
      (data as ExerciseChartDataPoint[]).map((d) => d.minutesPerWeek),
      EXERCISE_Y_DOMAIN
    );
  }
  return calculateDynamicYDomain(
    (data as SleepChartDataPoint[]).map((d) => d.sleepHours),
    SLEEP_Y_DOMAIN
  );
}

/**
 * Builds zone label band center configs for exercise/sleep charts.
 */
export function buildBandCenters(
  variant: "exercise" | "sleep",
  t: TranslationFn
) {
  if (variant === "exercise") {
    return [
      {
        x: 5,
        y: 135,
        label: t("HealthChart.exerciseChart.zone.inactive"),
        color: themeColors.error,
      },
      {
        x: 5,
        y: 250,
        label: t("HealthChart.exerciseChart.zone.low"),
        color: themeColors.warning,
      },
      {
        x: 5,
        y: 350,
        label: t("HealthChart.exerciseChart.zone.healthy"),
        color: themeColors.success,
      },
    ];
  }
  return [
    {
      x: 5,
      y: 3.5,
      label: t("HealthChart.sleepChart.zone.low"),
      color: themeColors.error,
    },
    {
      x: 5,
      y: 8,
      label: t("HealthChart.sleepChart.zone.okay"),
      color: themeColors.warning,
    },
    {
      x: 5,
      y: 10.5,
      label: t("HealthChart.sleepChart.zone.healthy"),
      color: themeColors.success,
    },
  ];
}
